/**
 * The compact editor's one panel: a sheet under the room, or beside it on a
 * phone held sideways.
 *
 * It takes its place in the layout instead of lying over the canvas. Opening it
 * makes the canvas shorter, so everything that works out "the middle of what
 * the planner can see" — where a new table lands, Fit room, the zoom buttons,
 * how far the room may be panned — stays right without knowing a sheet exists.
 *
 * Half height by default, so the table being edited stays in sight above it.
 * The grip follows a finger and snaps to closed, half or full when let go; the
 * chevron does the same for anyone not dragging. While someone types in it the
 * sheet takes the whole height and pads itself above the on-screen keyboard,
 * which on iOS and Android overlays the page rather than resizing it.
 */

import { type ReactNode, useEffect, useRef, useState } from 'react'

import { useT } from '../lib/i18n/I18nProvider'

interface Props {
  open: boolean
  /** Docked beside the room instead of under it. */
  side: boolean
  expanded: boolean
  title: string
  /**
   * The contents carry their own heading (the inspector names what is selected),
   * so the sheet's stays for screen readers and its buttons float at the corner.
   */
  titleHidden?: boolean
  onClose: () => void
  onExpandedChange: (expanded: boolean) => void
  children: ReactNode
}

/** The half-height sheet: this share of the space under the top bar, never taller than the cap. */
const HALF_SHARE = 0.56
const HALF_CAP = 520
const SIDE_WIDTH = 'min(380px, 46%)'

/** Covered by more than this, the viewport is losing space to a keyboard, not to browser chrome. */
const KEYBOARD_MIN = 120

export function Sheet({
  open,
  side,
  expanded,
  title,
  titleHidden = false,
  onClose,
  onExpandedChange,
  children,
}: Props) {
  const t = useT()
  const ref = useRef<HTMLElement>(null)
  // The content stays through the closing slide, and is dropped once it is out of sight.
  const [mounted, setMounted] = useState(open)
  const [typing, setTyping] = useState(false)
  const grip = useRef<{ pointerId: number; startY: number; startHeight: number; room: number } | null>(
    null,
  )

  useEffect(() => {
    if (open) setMounted(true)
  }, [open])

  // --- the keyboard ----------------------------------------------------------
  useEffect(() => {
    const viewport = window.visualViewport
    const node = ref.current
    if (!open || !viewport || !node) return undefined

    let timer: ReturnType<typeof setTimeout> | undefined
    const update = () => {
      const covered = Math.max(0, window.innerHeight - viewport.height)
      const focused = document.activeElement
      const editing =
        focused instanceof HTMLElement &&
        node.contains(focused) &&
        (focused.tagName === 'INPUT' || focused.tagName === 'TEXTAREA')
      const next = editing && covered > KEYBOARD_MIN
      setTyping(next)
      node.style.setProperty('--keyboard-inset', next ? `${covered - viewport.offsetTop}px` : '0px')
    }
    // After a blur the next field is not focused yet; look once focus has landed.
    const later = () => {
      clearTimeout(timer)
      timer = setTimeout(update, 0)
    }
    // Once the keyboard is up, bring the field into the part of the sheet it did not cover.
    const reveal = (event: FocusEvent) => {
      const target = event.target
      if (!(target instanceof HTMLElement) || !node.contains(target)) return
      setTimeout(() => target.scrollIntoView({ block: 'nearest' }), 320)
    }

    viewport.addEventListener('resize', update)
    viewport.addEventListener('scroll', update)
    document.addEventListener('focusin', later)
    document.addEventListener('focusout', later)
    node.addEventListener('focusin', reveal)
    update()
    return () => {
      clearTimeout(timer)
      viewport.removeEventListener('resize', update)
      viewport.removeEventListener('scroll', update)
      document.removeEventListener('focusin', later)
      document.removeEventListener('focusout', later)
      node.removeEventListener('focusin', reveal)
    }
  }, [open])

  // --- the grip --------------------------------------------------------------
  const onGripDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const node = ref.current
    const room = node?.parentElement?.clientHeight
    if (!node || !room) return
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    grip.current = { pointerId: event.pointerId, startY: event.clientY, startHeight: node.offsetHeight, room }
    node.style.transition = 'none'
  }

  const onGripMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const session = grip.current
    const node = ref.current
    if (!session || !node || session.pointerId !== event.pointerId) return
    const height = session.startHeight - (event.clientY - session.startY)
    node.style.height = `${Math.min(session.room, Math.max(0, height))}px`
  }

  const onGripUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const session = grip.current
    const node = ref.current
    if (!session || !node || session.pointerId !== event.pointerId) return
    grip.current = null
    const height = node.offsetHeight
    node.style.transition = ''
    node.style.height = ''

    // Barely moved: a tap on the grip, which toggles like the chevron.
    if (Math.abs(event.clientY - session.startY) < 6) {
      onExpandedChange(!expanded)
      return
    }
    const half = Math.min(session.room * HALF_SHARE, HALF_CAP)
    if (height < half * 0.5) onClose()
    else onExpandedChange(height > (half + session.room) / 2)
  }

  const onTransitionEnd = (event: React.TransitionEvent<HTMLElement>) => {
    if (event.target !== event.currentTarget) return
    if (event.propertyName !== (side ? 'width' : 'height')) return
    if (!open) setMounted(false)
  }

  const size = !open ? '0px' : side ? SIDE_WIDTH : expanded || typing ? '100%' : `min(${HALF_SHARE * 100}%, ${HALF_CAP}px)`

  return (
    <section
      ref={ref}
      id="editor-sheet"
      className={[
        'sheet',
        side ? 'sheet--side' : 'sheet--bottom',
        open ? 'is-open' : '',
        expanded || typing ? 'is-expanded' : '',
        typing ? 'is-typing' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={side ? { width: size } : { height: size }}
      aria-label={title}
      aria-hidden={!open}
      onTransitionEnd={onTransitionEnd}
    >
      {mounted && (
        <div className="sheet-inner">
          {!side && (
            <div
              className="sheet-grip"
              onPointerDown={onGripDown}
              onPointerMove={onGripMove}
              onPointerUp={onGripUp}
              onPointerCancel={onGripUp}
              aria-hidden="true"
            >
              <span />
            </div>
          )}
          <header className={`sheet-head${titleHidden ? ' sheet-head--floating' : ''}`}>
            <h2 className={titleHidden ? 'sr-only' : 'sheet-title'}>{title}</h2>
            {!side && (
              <button
                type="button"
                className="sheet-btn"
                onClick={() => onExpandedChange(!expanded)}
                aria-label={expanded ? t.panels.collapse : t.panels.expand}
                aria-expanded={expanded}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                  <path
                    d={expanded ? 'M6 9l6 6 6-6' : 'M6 15l6-6 6 6'}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.9"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
            <button type="button" className="sheet-btn" onClick={onClose} aria-label={t.common.close}>
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
              </svg>
            </button>
          </header>
          <div className="sheet-body">{children}</div>
        </div>
      )}
    </section>
  )
}
